/**
 * DASHBOARD COMPONENT
 * Analytics and insights dashboard
 */

import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Brain, Code, Zap, Users, Clock } from 'lucide-react';

export function Dashboard() {
  const stats = [
    { label: 'AI Interactions', value: '0', icon: Brain, color: 'from-blue-500 to-purple-600' },
    { label: 'Code Generated', value: '0', icon: Code, color: 'from-green-500 to-teal-600' },
    { label: 'Processing Speed', value: '0ms', icon: Zap, color: 'from-yellow-500 to-orange-600' },
    { label: 'Active Users', value: '0', icon: Users, color: 'from-pink-500 to-red-600' },
  ];

  return (
    <div className="h-full p-6 bg-gradient-to-b from-slate-900/50 to-slate-800/50 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">CodeMind AI Dashboard</h1>
          <p className="text-slate-400">Real-time analytics and performance insights</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 border border-white/20 rounded-lg p-6 backdrop-blur-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* AI Usage Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white/10 border border-white/20 rounded-lg p-6 backdrop-blur-sm"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              <span>AI Usage Trends</span>
            </h3>
            <div className="h-64 flex items-end justify-between space-x-2">
              {[0, 0, 0, 0, 0, 0, 0].map((height, index) => (
                <motion.div
                  key={index}
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="bg-gradient-to-t from-blue-500 to-purple-600 rounded-t flex-1 min-h-[4px]"
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-2">
              <span>Mon</span>
              <span>Tue</span>
              <span>Wed</span>
              <span>Thu</span>
              <span>Fri</span>
              <span>Sat</span>
              <span>Sun</span>
            </div>
          </motion.div>

          {/* Performance Metrics */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white/10 border border-white/20 rounded-lg p-6 backdrop-blur-sm"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Clock className="w-5 h-5 text-green-400" />
              <span>Performance Metrics</span>
            </h3>
            <div className="space-y-4">
              {[
                { label: 'Average Response Time', value: '0ms', percentage: 0 },
                { label: 'Code Quality Score', value: '0%', percentage: 0 },
                { label: 'User Satisfaction', value: '0/5', percentage: 0 },
                { label: 'Uptime', value: '0%', percentage: 0 },
              ].map((metric, index) => (
                <div key={metric.label} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-300">{metric.label}</span>
                    <span className="text-white font-medium">{metric.value}</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${metric.percentage}%` }}
                      transition={{ delay: 0.8 + index * 0.1, duration: 0.8 }}
                      className="bg-gradient-to-r from-green-500 to-teal-600 h-2 rounded-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white/10 border border-white/20 rounded-lg p-6 backdrop-blur-sm"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Recent AI Activity</h3>
          <div className="space-y-3">
            <div className="text-center py-8 text-slate-400">
              No activity yet. Start using the AI to see analytics here.
            </div>
            {[].map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg"
              >
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'success' ? 'bg-green-500' :
                  activity.type === 'warning' ? 'bg-yellow-500' :
                  activity.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
                }`} />
                <div className="flex-1">
                  <div className="text-sm text-white">{activity.action}</div>
                  <div className="text-xs text-slate-400">{activity.time}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}